/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { getStrings } from "@microsoft/shifts-experiences-shared";
import { ListItem, LoadingState } from "@microsoft/shifts-models-view";
import { CrossLocationTeamsStore, crossLocationTeamsActions } from "@microsoft/shifts-states-openshifts";
import { useEffect } from "react";
import AllDataServicesInstance from "sh-services/dataservice/AllDataServicesInstance";
import { TeamStore } from "sh-stores";

import { CrossLocationTeamNameProps } from "./CrossLocationTeamNameProps";
import { ECSConfigKey, ECSConfigService } from "../../../../sh-services";

/**
 * Retrieves the cross-location team name if the shift is from an external team, else returns undefined.
 * @param props The properties.
 * @returns The cross-location team name if the shift is from an external team, else undefined.
 */
export const useCrossLocationTeamName = (props: CrossLocationTeamNameProps): string | undefined => {
    const { crossLocationTeamId } = props;
    const teamId = TeamStore().teamId;
    const { loadingState, valuesByIds } = CrossLocationTeamsStore();
    let teamName: string | undefined = valuesByIds.get(crossLocationTeamId)?.label;
    const clsEnhancementEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.CrossLocationMarketplaceEnhancementsEnabled);

    useEffect(() => {
        if (crossLocationTeamId && teamId && loadingState == LoadingState.NotStarted) {
            if (clsEnhancementEnabled) {
                loadCrossLocationTeamsV2(teamId);
                return;
            }
            loadCrossLocationTeams(teamId);
        }
    }, [crossLocationTeamId, loadingState, teamId]);

    if (!crossLocationTeamId) {
        return undefined;
    } else if (!teamName && loadingState == LoadingState.Loading) {
        teamName = getStrings("crossLocationTeam", "teamLoading");
    } else if (!teamName && loadingState != LoadingState.NotStarted) {
        teamName = getStrings("crossLocationTeam", "teamUnavailable");
    }

    return teamName;
};

/**
 * Loads the cross-location teams from given home team id.
 * @param teamId The currently selected team id.
 */
const loadCrossLocationTeams = async (teamId: string): Promise<void> => {
    const { valuesByIds } = CrossLocationTeamsStore();
    const team = valuesByIds.get(teamId);

    if (team || !teamId) {
        return;
    }

    const { CrossLocationTeamDataService } = AllDataServicesInstance;

    const setDataInState = (teams: ListItem[]): void => {
        const teamsIds = teams.map(({ value }) => value);

        crossLocationTeamsActions.updateMap(teams, ({ value }) => value);
        crossLocationTeamsActions.loadSucceed(teamsIds);
    };

    try {
        crossLocationTeamsActions.loadStarted();

        await CrossLocationTeamDataService.getCrossLocationTeams(teamId, setDataInState);
    } catch (error) {
        crossLocationTeamsActions.loadFailed(error);
    }
};

/**
 * Loads the cross-location teams from given home team id.
 * @param teamId The currently selected team id.
 */
const loadCrossLocationTeamsV2 = async (teamId: string): Promise<void> => {
    const { valuesByIds } = CrossLocationTeamsStore();
    const team = valuesByIds.get(teamId);

    if (team || !teamId) {
        return;
    }

    const { CrossLocationTeamDataService } = AllDataServicesInstance;

    const setDataInState = (teams: ListItem[]): void => {
        const teamsIds = teams.map(({ value }) => value);

        crossLocationTeamsActions.updateMap(teams, ({ value }) => value);
        crossLocationTeamsActions.loadSucceed(teamsIds);
    };

    try {
        crossLocationTeamsActions.loadStarted();

        await CrossLocationTeamDataService.getCrossLocationTeamsV2(teamId, setDataInState);
    } catch (error) {
        crossLocationTeamsActions.loadFailed(error);
    }
};